<script lang="ts">
  import { session, twApiClient } from "/@/lib/tastyworks-rest"
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import AwaitItemsResponse from "/@lib/components/loading/AwaitItemsResponse.svelte"
  import LoadingSpinner from "/@lib/components/loading/LoadingSpinner.svelte"
  import ClickableTable from "/@lib/components/table/ClickableTable.svelte"
  import { TABLE_STORAGE_KEYS } from "/@lib/components/table/column-layout"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import {
    getDesktopUrl,
    getElectronUrl,
    getWebBasedTwoUrl,
    launchAccountBasedOnRegulatoryDomain,
    launchApexClearing,
    launchDesktopApplication,
    launchElectronApp,
    launchWebTwoPointOh,
  } from "/@lib/utils/applicationLauncher"
  import {
    formatDateTime,
    formatName,
    formatReceivedTime,
  } from "/@lib/utils/formats"
  import { accountDocumentsUrl } from "/@lib/utils/urls"
  import { Close, Locked } from "@tastyworks/icons-2.0"
  import {
    ItemsResponse,
    type CustomerAccountRowResult,
  } from "@tastyworks/tastyworks-api"
  import { Button, Dialog } from "@tastyworks/ui-library"
  import VanillaContextMenu from "vanilla-context-menu"
  import type { MenuItem } from "vanilla-context-menu/dist/@types/interface"

  type SearchMode = "default" | "account" | "customer"

  type Props = {
    customerAccountPromise: Promise<ItemsResponse<CustomerAccountRowResult> | null>
    searchMode?: SearchMode
    searchModeStatus?: string | null
    onCustomerSelected?: () => void
    onSearchOnlyAccount?: (accountNumber: string) => void
    onSearchOnlyCustomer?: (row: CustomerAccountRowResult) => void
    onDismissSearchMode?: () => void
  }

  let {
    customerAccountPromise,
    searchMode = "default",
    searchModeStatus = null,
    onCustomerSelected,
    onSearchOnlyAccount,
    onSearchOnlyCustomer,
    onDismissSearchMode,
  }: Props = $props()

  // Local menu-row shape: a "real" action row, or the literal `"hr"`
  // separator string (matches vanilla-context-menu's MenuItem union).
  // Keeping the action shape distinct from the library's MenuItem keeps
  // disabled-state styling logic in this component rather than scattered
  // into every menu-definition entry.
  type CustomerMenuRow =
    | {
        label: string
        disabled: boolean
        callback: () => void
      }
    | "hr"

  function sendPasswordReset() {
    if ($selectedCustomerAccount.user?.id) {
      twApiClient.AdminUserService.passwordReset(
        $selectedCustomerAccount.user?.id
      )
    }
  }
  function unlockUser() {
    if ($selectedCustomerAccount.user?.id) {
      twApiClient.AdminUserService.unlockUser($selectedCustomerAccount.user?.id)
    }
  }

  function kickSession() {
    if ($selectedCustomerAccount.user?.id) {
      twApiClient.AdminUserService.kickSession(
        $selectedCustomerAccount.user?.id
      )
    }
  }

  function getOtp() {
    if ($selectedCustomerAccount.user?.id) {
      return twApiClient.AdminUserService.resetOtp(
        $selectedCustomerAccount.user?.id
      )
    }
  }
  function confirmEmail() {
    if ($selectedCustomerAccount.user?.email) {
      twApiClient.AdminUserService.emailConfirmation(
        $selectedCustomerAccount?.user?.email
      )
    } else if ($selectedCustomerAccount.customer?.email) {
      twApiClient.AdminUserService.emailConfirmation(
        $selectedCustomerAccount.customer?.email
      )
    }
  }

  function searchOnlyAccount() {
    const accountNum = $selectedCustomerAccount.account?.accountNumber
    if (!accountNum) {
      return
    }
    onSearchOnlyAccount?.(accountNum)
  }

  function searchOnlyCustomer() {
    if ($selectedCustomerAccount.customer?.id) {
      onSearchOnlyCustomer?.($selectedCustomerAccount)
    }
  }

  // Mirrors the fall-through in App.svelte's formatCustomerStatus so the
  // menu label and the eventual status bar text stay aligned for rows that
  // don't have a username.
  function formatCustomerMenuLabel(row: CustomerAccountRowResult): string {
    const identifier = row.user?.username || row.user?.email || row.customer?.id
    return identifier
      ? `Search Only Customer ${identifier}`
      : "Search Only Customer"
  }

  function viewAccountDocuments() {
    const url = accountDocumentsUrl(
      $selectedCustomerAccount.account?.accountNumber
    )
    if (!url) {
      return
    }
    window.open(url, "_blank", "noopener,noreferrer")
  }

  function openWebBased() {
    launchWebTwoPointOh(
      getWebBasedTwoUrl(),
      $selectedCustomerAccount.account?.accountNumber ?? "",
      session.sessionToken
    )
  }

  function openDesktop() {
    launchDesktopApplication(
      getDesktopUrl(),
      $selectedCustomerAccount.account?.accountNumber ?? "",
      session.sessionToken
    )
  }

  function openElectron() {
    launchElectronApp(
      getElectronUrl(),
      $selectedCustomerAccount.account?.accountNumber ?? "",
      session.sessionToken
    )
  }

  function openApexClearing() {
    launchApexClearing($selectedCustomerAccount.account?.accountNumber ?? "")
  }

  const COLUMNS: ColumnDefinitions = [
    {
      name: "Name",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        formatName(
          profile.customer?.firstName,
          profile.customer?.lastName,
          profile.customer?.middleName
        ),
    },
    {
      name: "Account Number",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.account?.accountNumber,
    },
    {
      name: "Mobile Phone",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.customer?.mobilePhoneNumber,
    },
    {
      name: "Home Phone",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.customer?.homePhoneNumber,
    },
    {
      name: "Work Phone",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.customer?.workPhoneNumber,
    },
    {
      name: "Email",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.user?.email || profile.customer?.email,
    },
    {
      name: "Username",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.user?.username,
    },
    {
      name: "LTA",
      dataFormatter: (profile: CustomerAccountRowResult) =>
        profile.authorities?.find(
          (authority) =>
            authority.customerId.toString() === profile.customer?.id &&
            profile.account?.accountAuthorities.includes(authority.id)
        )?.authorityLevel ?? profile.account?.authorityLevel,
    },
  ]

  let opens = $state(Array(5).fill(false))

  // Context-menu rows grouped by purpose. `"hr"` markers between groups
  // render as a thin separator line (vanilla-context-menu natively
  // supports the literal string `"hr"` in its menuItems array) so the
  // 13-item menu reads as five visually distinct clusters rather than a
  // continuous wall of text:
  //
  //   1. Search modifiers   — narrow the result set to one customer or
  //                           account.
  //   2. Open in app        — launch the selected account in another
  //                           tastytrade client.
  //   3. Account state      — mutate or view the account itself.
  //   4. User state         — mutate the user behind the account.
  //   5. External           — Apex Clearing (lives alone since it leaves
  //                           the tastytrade stack entirely).
  const getCustomerMenuItems = (): CustomerMenuRow[] => {
    return [
      // Group 1: search modifiers (customer first per Jason's request —
      // the broader "all accounts for this customer" filter is the more
      // common starting point; "single account" sits below it as the
      // narrower follow-up filter).
      {
        label: formatCustomerMenuLabel($selectedCustomerAccount),
        disabled:
          !$selectedCustomerAccount.customer?.id || searchMode === "customer",
        callback: searchOnlyCustomer,
      },
      {
        label:
          "Search Only Account " +
          $selectedCustomerAccount.account?.accountNumber,
        disabled:
          !$selectedCustomerAccount.account?.accountNumber ||
          searchMode === "account",
        callback: searchOnlyAccount,
      },
      "hr",
      // Group 2: open in another tastytrade app
      {
        label: "Open Account in Web-Based",
        disabled: !$selectedCustomerAccount.account?.accountNumber,
        callback: openWebBased,
      },
      {
        label: "Open in Desktop",
        disabled: !$selectedCustomerAccount.account?.accountNumber,
        callback: openDesktop,
      },
      {
        label: "Open in Desktop 2.0",
        disabled: !$selectedCustomerAccount.account?.accountNumber,
        callback: openElectron,
      },
      "hr",
      // Group 3: account-level state actions
      {
        label: "View Account Documents",
        disabled: !$selectedCustomerAccount.account?.accountNumber,
        callback: viewAccountDocuments,
      },
      "hr",
      // Group 4: user-level state actions
      {
        label: "Send Password Reset",
        disabled: !$selectedCustomerAccount.user?.id,
        callback: () => (opens[0] = true),
      },
      {
        label: "Send Email Confirmation",
        disabled:
          $selectedCustomerAccount.user?.isConfirmed === true ||
          !$selectedCustomerAccount.user,
        callback: () => (opens[1] = true),
      },
      {
        label: "Kick Session",
        disabled: !$selectedCustomerAccount.user,
        callback: () => (opens[2] = true),
      },
      {
        label: "Unlock User",
        disabled:
          !$selectedCustomerAccount.user ||
          !$selectedCustomerAccount.user.isLocked,
        callback: () => (opens[3] = true),
      },
      {
        label: "Retrieve 2-Factor Code",
        disabled: !$selectedCustomerAccount.user,
        callback: () => (opens[4] = true),
      },
      "hr",
      // Group 5: external clearing-system tool
      {
        label: "Open in Apex Clearing",
        disabled: !$selectedCustomerAccount.account?.accountNumber,
        callback: openApexClearing,
      },
    ]
  }

  const getDialogs = () => [
    {
      title: "Send password reset link",
      func: sendPasswordReset,
    },
    {
      title: "Resend email confirmation for this user",
      func: confirmEmail,
    },
    {
      title: "Kill all sessions for this user",
      func: kickSession,
    },
    {
      title: "Unlock user: " + $selectedCustomerAccount.user?.id,
      func: unlockUser,
    },
  ]

  let menu: null | VanillaContextMenu = $state(null)

  const updateMenu = () => {
    let items: MenuItem[] = []
    getCustomerMenuItems().forEach((row) => {
      if (row === "hr") {
        // vanilla-context-menu treats the literal "hr" as a divider —
        // passed through as-is alongside the normal action objects.
        items.push("hr")
        return
      }
      const { label, disabled, callback } = row
      if (!disabled) {
        items.push({ label, callback })
      } else {
        items.push({
          label: "",
          callback: () => undefined,
          iconHTML: `<span class="disabled-button">${label}</span>`,
          preventCloseOnClick: true,
        })
      }
    })
    // Tear down the previous instance before replacing it so we don't leak
    // contextmenu listeners on rows the user has moved away from.
    menu?.off()
    menu = new VanillaContextMenu({
      customClass: "context-menu",
      menuItems: items,
      normalizePosition: false,
      scope: document.activeElement as HTMLElement,
      theme: "white",
      transitionDuration: 0,
    })
  }
</script>

{#each getDialogs() as { title, func }, i}
  <Dialog.Root bind:open={opens[i]}>
    <Dialog.Portal>
      <Dialog.Overlay />
      <Dialog.Content>
        <div class="flex justify-between text-xl">
          {title}
          <Dialog.Close />
        </div>
        <p>Are you sure you want to do this?</p>
        <div class="flex w-full flex-row justify-center gap-3">
          <Dialog.Close>
            <Button variant="defaultOutline">No</Button>
          </Dialog.Close>
          <Dialog.Close>
            <Button
              name="submit"
              type="submit"
              variant="defaultOutline"
              onclick={func}>Yes</Button
            ></Dialog.Close
          >
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
{/each}

<div
  role="button"
  class="flex h-full flex-grow flex-col"
  tabindex={0}
  onkeydown={(e) => {
    e.key === "Tab" && menu?.close()
  }}
>
  {#if searchModeStatus}
    <div
      class="flex cursor-default items-center justify-between gap-2 border-x-2 border-t-2 bg-trade-desk-gray px-3 py-1 text-sm"
      data-testid="search-mode-status"
    >
      <span>{searchModeStatus}</span>
      <button
        type="button"
        class="flex h-5 w-5 cursor-pointer items-center justify-center rounded hover:bg-trade-desk-gray-dark"
        aria-label="Dismiss filtered search"
        data-testid="search-mode-dismiss"
        onclick={() => onDismissSearchMode?.()}
      >
        <Close class="h-3 w-3" />
      </button>
    </div>
  {/if}
  <div class="flex h-[90px] flex-grow border-2" id="customer-table">
    <AwaitItemsResponse
      promise={customerAccountPromise}
      defaultMessage="Search for customers"
      noResultsMessage="No customers found"
    >
      {#snippet children({ items }: { items: CustomerAccountRowResult[] })}
        <ClickableTable
          storageKey={TABLE_STORAGE_KEYS.customers}
          columns={COLUMNS}
          {items}
          onItemSelected={(item) => {
            $selectedCustomerAccount = item as CustomerAccountRowResult
            onCustomerSelected?.()
            updateMenu()
          }}
          onItemDoubleClicked={() => {
            if (!$selectedCustomerAccount.account?.accountNumber) return
            launchAccountBasedOnRegulatoryDomain(
              $selectedCustomerAccount.account?.accountNumber,
              session.sessionToken
            )
          }}
        >
          {#snippet leadingCell(row: CustomerAccountRowResult)}
            {#if row.user?.isLocked}
              <span
                title={row.user.lockedAt
                  ? `Locked At: ${formatDateTime(row.user.lockedAt)}`
                  : "Locked"}
                aria-label="Locked"
                data-testid="customer-status-locked"
                class="text-general-secondary-text"
              >
                <Locked aria-hidden="true" class="h-5 w-5" />
              </span>
            {/if}
          {/snippet}
        </ClickableTable>
      {/snippet}
    </AwaitItemsResponse>
  </div>

  <Dialog.Root bind:open={opens[4]}>
    <Dialog.Portal>
      <Dialog.Overlay />
      <Dialog.Content>
        {#await getOtp()}
          <div class="relative h-20 w-full self-center">
            <LoadingSpinner size="40px" />
          </div>
        {:then results}
          <div class="flex justify-between text-xl">
            {#if results?.isError}
              Error: This user has not yet enabled two-factor authentication
            {:else}
              Code: {results?.data?.code} <br />
              Expires At: {formatReceivedTime(results?.data?.["expires-at"])}
            {/if}
            <Dialog.Close />
          </div>
        {/await}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
</div>
